import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { AlertCircle, MousePointer2, Navigation, RefreshCw, Scroll, Type } from 'lucide-react';
import type { DetrixSession, DetrixUserStep } from '../../types';
import { usePlaybackStore } from '../../store/usePlaybackStore';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatSessionOffset } from '../../utils/time';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const KindIcon = ({ kind }: { kind: DetrixUserStep['kind'] }) => {
  switch (kind) {
    case 'click':
      return <MousePointer2 className="h-4 w-4" />;
    case 'type':
      return <Type className="h-4 w-4" />;
    case 'scroll':
      return <Scroll className="h-4 w-4" />;
    case 'navigate':
      return <Navigation className="h-4 w-4" />;
    case 'update_page_state':
      return <RefreshCw className="h-4 w-4" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-[var(--error)]" />;
    default:
      return null;
  }
};

export const StepsPanel: React.FC<{ session: DetrixSession }> = ({ session }) => {
  const { steps = [] } = session;
  const { focusedTimestamp, isPlaying } = usePlaybackStore();
  const { selectedId, setSelectedId } = useSessionUiStore();
  const playheadStepRef = useRef<HTMLDivElement | null>(null);

  const playheadStepId = useMemo(() => {
    if (focusedTimestamp == null) return null;
    const step = steps.find(
      (s) => focusedTimestamp >= s.timestampStart && focusedTimestamp <= s.timestampEnd,
    );
    return step?.id ?? null;
  }, [focusedTimestamp, steps]);

  useLayoutEffect(() => {
    if (!playheadStepId) return;
    playheadStepRef.current?.scrollIntoView({ block: 'nearest', behavior: isPlaying ? 'auto' : 'smooth' });
  }, [focusedTimestamp, isPlaying, playheadStepId]);

  useLayoutEffect(() => {
    if (!isPlaying || !playheadStepId || playheadStepId === selectedId) return;
    setSelectedId(playheadStepId);
  }, [isPlaying, playheadStepId, selectedId, setSelectedId]);

  useLayoutEffect(() => {
    if (!selectedId) return;
    const step = steps.find((s) => s.id === selectedId);
    if (!step || step.kind !== 'error') return;
    document.querySelector(`[data-step-id="${selectedId}"]`)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedId, steps]);

  if (steps.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center text-[var(--text-muted)]">
        <Navigation className="mb-2 h-8 w-8 opacity-20" />
        <p>No user steps captured in this session.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--bg-editor)]">
      {steps.map((step, index) => {
        const atPlayhead = playheadStepId === step.id;
        const errorSelected = selectedId === step.id && step.kind === 'error';
        return (
        <div
          key={step.id}
          data-step-id={step.id}
          ref={atPlayhead ? (el) => { playheadStepRef.current = el; } : undefined}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedId(step.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedId(step.id);
            }
          }}
          className={cn(
            'group relative flex cursor-pointer flex-col gap-2 border-b border-[var(--border-subtle)] p-3 transition-all',
            selectedId === step.id && !errorSelected
              ? 'bg-[var(--list-row-selected-bg)] outline-1 outline-[var(--accent)]'
              : '',
            errorSelected ? 'bg-[var(--list-row-error-bg)] outline outline-1 outline-[var(--list-row-error-ring)]' : '',
            selectedId !== step.id ? 'hover:bg-[var(--bg-elevated)]' : '',
            atPlayhead && selectedId !== step.id ? 'bg-[var(--accent)]/8 ring-1 ring-[var(--accent)]/35' : '',
          )}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[var(--bg-sidebar)] text-[11px] font-bold text-[var(--text-muted)]">
                {index + 1}
              </div>
              <div>
                <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">{step.title}</h3>
                {step.subtitle ? <p className="text-[11px] text-[var(--text-secondary)]">{step.subtitle}</p> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <KindIcon kind={step.kind} />
              <span className="tabular-nums text-[11px] text-[var(--text-muted)]">
                {formatSessionOffset(step.timestampStart)}
              </span>
            </div>
          </div>

          {step.target ? (
            <div className="mt-1 flex items-center gap-2 overflow-hidden rounded border border-[var(--border)] bg-[var(--bg-sidebar)] px-2 py-0.5">
              <span className="text-[10px] font-bold uppercase text-[var(--text-muted)]">Target</span>
              <code className="mono-small truncate text-[var(--accent)]">{step.target}</code>
            </div>
          ) : null}
        </div>
      );
      })}
    </div>
  );
};
